import { SearchableKeyword, SearchableViewItem, SearchableViewItems } from './types';

// Escape regex special characters in searchTerm to avoid breaking the RegExp
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}


function isStringMatch(keyword: SearchableKeyword, searchTerm: string): boolean {
  if (!keyword.label || !keyword.translatedLabel || !searchTerm) {
    return false;
  }

  // Replace spaces in the search term with a pattern that matches zero or more spaces. This allows for matching partial words with optional spaces in between.
  const normalizedSearchTerm = escapeRegExp(searchTerm.toLowerCase()).replace(/\s+/g, '\\s*');
  const regex = new RegExp(normalizedSearchTerm);

  const label = keyword.label.toLowerCase();
  const translatedLabel = keyword.translatedLabel.toLowerCase();

  // For label and translatedLabel return an array of matches if any are found, or null if no matches are found.
  const labelMatch = label.match(regex);
  const translatedLabelMatch = translatedLabel.match(regex);

  // If labelMatch is not null, indicating that matches were found, labelScore is set to the length of the array containing the matches. This gives a crude indication of how many matches were found.
  // If labelMatch is null, indicating no matches were found, labelScore is set to 0.
  const labelScore = labelMatch ? labelMatch.length : 0;
  const translatedLabelScore = translatedLabelMatch ? translatedLabelMatch.length : 0;

  // The relevanceScore is calculated as the maximum of labelScore and translatedLabelScore.
  // This ensures that the highest score from either the label or translatedLabel is used as the final relevance score.
  const relevanceScore = Math.max(labelScore, translatedLabelScore);

  return relevanceScore > 0;
}

export function filterTreeNodes(
  nodesArray: SearchableViewItems[],
  searchString: string,
  searchStringMinLength = 0
): SearchableViewItems[] {
  if (searchString.length < searchStringMinLength) {
    return [];
  }

  const isNodeMatching = (node: SearchableViewItem): boolean => {
    if (
      Array.isArray(node.searchableKeywords) &&
      node.searchableKeywords.some((keyword: SearchableKeyword) => isStringMatch(keyword, searchString))
    ) {
      return true;
    }

    return false;
  };

  const getNodes = (result: SearchableViewItems[], node: SearchableViewItems): SearchableViewItems[] => {
    if (isNodeMatching(node)) {
      result.push(node);
    }

    if (Array.isArray(node.children)) {
      const children: SearchableViewItems[] = node.children.reduce(getNodes, []);
      if (children.length) {
        result.push({ ...node, children });
      }
    }

    return result;
  };

  return nodesArray.reduce(getNodes, []);
}
